import React, {useContext, useState} from "react";
import {injectIntl} from "react-intl";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import {GlobalStateContext} from "../../../global/context/GlobalStateContext";
import {getLocalizedMessage} from "../../../util/Localization";
import {ALERT_UPDATE_SUCCESS_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE} from "../../../util/Constants";
import {updateVaprBrightnessDetails} from "../network/VaprDeviceNetworkAPI";

const MIN_BRIGHTNESS = 0;
const MAX_BRIGHTNESS = 10;
// Matches the edge default (VaprConstants.Defaults.SCENE_LIGHTING_LEVEL = 0.3f = 3/10 in SVAS):
// when a device has no brightness set, the camera runs at 3, so the UI must show 3 — not 0,
// which would misrepresent an actually-lit device and be indistinguishable from a hardware fault.
const DEFAULT_BRIGHTNESS = "3";

// Resolves the starting slider value: the device's current level when it is a valid 0–10
// integer, otherwise the default (3) when the device has no brightness set.
const resolveInitialLevel = (currentLevel) => {
    const numeric = Number(currentLevel);
    if (currentLevel !== "" && currentLevel !== null && currentLevel !== undefined
        && Number.isInteger(numeric) && numeric >= MIN_BRIGHTNESS && numeric <= MAX_BRIGHTNESS) {
        return String(numeric);
    }
    return DEFAULT_BRIGHTNESS;
};

/**
 * "Adjust Brightness" popup for a single VAPR device. Presents a slider (0–10 with per-step
 * marks) and a numeric input kept in sync — moving the slider updates the input and vice versa.
 * Save changes a device setting, so it first asks for confirmation (showWarning); on confirm it
 * calls updateVaprBrightnessDetails directly (an in-place update of only the sceneBrightnessLevel
 * attribute, which then refetches the overview) and surfaces a success/failure toast (showCustomAlert)
 * so the operator knows whether the save landed. On Cancel it closes without side effects. The
 * network call is made here — not via a reducer case — so the reducer stays pure (a reducer side
 * effect would double-fire under React 18 StrictMode).
 *
 * @param open         whether the modal is visible
 * @param setOpen      setter to toggle visibility
 * @param mac          MAC of the device being adjusted
 * @param currentLevel the device's stored brightness ("" when unset → slider starts at the default 3)
 * @param dispatch     VAPR overview reducer dispatch (passed to the network layer for the refetch)
 */
const AdjustBrightnessModal = (props) => {
    const {open, setOpen, mac, currentLevel, dispatch, intl} = props;
    const {showWarning, showCustomAlert} = useContext(GlobalStateContext);
    const [level, setLevel] = useState(() => resolveInitialLevel(currentLevel));

    // Clamp any incoming value into 0–10 and drop non-numeric input; keeps slider and box aligned.
    // Clearing the box resolves to 0 (not blank) so the field always holds a concrete level.
    const setClampedLevel = (value) => {
        if (value === "") {
            setLevel(String(MIN_BRIGHTNESS));
            return;
        }
        const numeric = Number(value);
        if (Number.isNaN(numeric)) {
            return;
        }
        const clamped = Math.min(MAX_BRIGHTNESS, Math.max(MIN_BRIGHTNESS, Math.round(numeric)));
        setLevel(String(clamped));
    };

    const onCloseModal = () => {
        setOpen(false);
        setLevel(resolveInitialLevel(currentLevel));
    };

    const onClickSave = () => {
        // Level unchanged from the device's current value: nothing to write, so skip the confirm,
        // the NARC write and the overview refetch; just close and confirm with the success toast.
        if (level === resolveInitialLevel(currentLevel)) {
            onCloseModal();
            showCustomAlert(ALERT_UPDATE_SUCCESS_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE.SUCCESS);
            return;
        }
        // Confirm before saving, then report the outcome via a toast so the operator can tell
        // whether it worked (the modal used to just close silently).
        showWarning(getLocalizedMessage({intl, stringId: "device.editWarning"}), async () => {
            onCloseModal();
            // On failure errorMessage is the backend's reason when it sent one, else the generic message.
            const {isSuccess, errorMessage} = await updateVaprBrightnessDetails(dispatch, level, mac);
            showCustomAlert(
                isSuccess ? ALERT_UPDATE_SUCCESS_ASSET_MESSAGE : errorMessage,
                isSuccess ? MERIDIAN_ALERT_TYPE.SUCCESS : MERIDIAN_ALERT_TYPE.ERROR);
        });
    };

    return (
        <Modal
            title={getLocalizedMessage({intl, stringId: "actions.adjust_brightness"})}
            open={open}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            bodySpacingInset="medium">
            <Column spacing="medium" spacingInset="medium">
                <Text tag="label" type="b200" color="primary" htmlFor="vapr-brightness-slider">
                    {getLocalizedMessage({intl, stringId: "modal.scene_brightness_level"})}
                </Text>
                <Row widths={["fill", "80px"]} alignmentVertical="center" spacing="medium">
                    <Column spacing="none">
                        <input
                            id="vapr-brightness-slider"
                            type="range"
                            min={MIN_BRIGHTNESS}
                            max={MAX_BRIGHTNESS}
                            step={1}
                            list="vapr-brightness-marks"
                            value={level}
                            onChange={(e) => setClampedLevel(e.target.value)}
                            style={{width: "100%"}}
                            aria-label={getLocalizedMessage({intl, stringId: "modal.scene_brightness_level"})}
                        />
                        <datalist id="vapr-brightness-marks">
                            {Array.from({length: MAX_BRIGHTNESS - MIN_BRIGHTNESS + 1}, (_, i) => (
                                <option key={i} value={i} label={String(i)}/>
                            ))}
                        </datalist>
                        <Row alignmentHorizontal="justify">
                            {Array.from({length: MAX_BRIGHTNESS - MIN_BRIGHTNESS + 1}, (_, i) => (
                                <Text key={i} type="b100" color="secondary">{i}</Text>
                            ))}
                        </Row>
                    </Column>
                    <Input
                        value={level}
                        onChange={setClampedLevel}
                        type="number"
                        size="small"
                        aria-label={getLocalizedMessage({intl, stringId: "modal.scene_brightness_level"})}
                    />
                </Row>
            </Column>
            <ModalFooter>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" size="medium" onClick={onCloseModal}>
                        {getLocalizedMessage({intl, stringId: "modal.cancel"})}
                    </Button>
                    <Button type="primary" size="medium" onClick={onClickSave}>
                        {getLocalizedMessage({intl, stringId: "modal.save"})}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};

export default injectIntl(AdjustBrightnessModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/vaprdevice/modal/AdjustBrightnessModal.js